import React, { useRef, useState } from "react";
import { Download, Instagram, Facebook } from "lucide-react";
import html2canvas from "html2canvas";

/**
 * SocialExport
 * Renders 2 download buttons (Story 9:16 and Post 16:9) + two hidden poster
 * elements that html2canvas captures as PNG. The hidden posters live off-screen
 * at the exact target dimensions so the capture is pixel-perfect.
 *
 * Props:
 *   - filename: string (base name, no extension)
 *   - storyPoster: ReactNode — content for the 1080×1920 Story export
 *   - postPoster:  ReactNode — content for the 1200×630 Post export
 *   - theme: "light" | "dark" (button styling)
 */
export default function SocialExport({
  filename = "gai-social",
  storyPoster,
  postPoster,
  theme = "light",
  testidPrefix = "social-export",
}) {
  const storyRef = useRef(null);
  const postRef = useRef(null);
  const [busy, setBusy] = useState(null); // "story" | "post" | null

  const capture = async (ref, w, h, label) => {
    if (!ref.current) return;
    setBusy(label);
    try {
      // Temporarily make the poster render in the viewport flow so fonts/images
      // settle; html2canvas needs computed sizes to be real.
      const node = ref.current;
      const prev = node.style.cssText;
      node.style.cssText = `position:fixed;top:0;left:0;width:${w}px;height:${h}px;z-index:-1;opacity:0;pointer-events:none;`;
      // Wait a frame for layout
      await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 60)));
      const canvas = await html2canvas(node, {
        width: w,
        height: h,
        windowWidth: w,
        windowHeight: h,
        scale: 2,
        backgroundColor: null,
        useCORS: true,
        allowTaint: true,
        logging: false,
      });
      node.style.cssText = prev;
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png", 0.95));
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}-${label}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      console.error("Social export failed", e);
    } finally {
      setBusy(null);
    }
  };

  const btnStyle = theme === "dark"
    ? { background: "transparent", border: "1px solid rgba(255,255,255,0.35)", color: "var(--gai-white)" }
    : { background: "transparent", border: "1px solid var(--gai-grey-300)", color: "var(--gai-black)" };

  return (
    <>
      <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-buttons`}>
        <button
          type="button"
          onClick={() => capture(storyRef, 1080, 1920, "story-9x16")}
          disabled={!!busy}
          className="px-4 py-2.5 text-[0.65rem] tracking-[0.22em] uppercase font-bold flex items-center gap-2 transition-colors disabled:opacity-50 hover:bg-[var(--gai-red)] hover:text-[var(--gai-white)] hover:border-[var(--gai-red)]"
          style={btnStyle}
          data-testid={`${testidPrefix}-story`}
          aria-label="Télécharger Story 9:16"
        >
          <Instagram size={13} /> {busy === "story-9x16" ? "Export…" : "Story 9:16"}
        </button>
        <button
          type="button"
          onClick={() => capture(postRef, 1200, 630, "post-16x9")}
          disabled={!!busy}
          className="px-4 py-2.5 text-[0.65rem] tracking-[0.22em] uppercase font-bold flex items-center gap-2 transition-colors disabled:opacity-50 hover:bg-[var(--gai-red)] hover:text-[var(--gai-white)] hover:border-[var(--gai-red)]"
          style={btnStyle}
          data-testid={`${testidPrefix}-post`}
          aria-label="Télécharger Post 16:9"
        >
          <Facebook size={13} /> {busy === "post-16x9" ? "Export…" : "Post 16:9"}
        </button>
      </div>

      {/* Hidden posters — fixed off-screen at target dimensions.
          html2canvas needs them in the DOM with real pixel sizes. */}
      <div
        aria-hidden
        style={{
          position: "fixed",
          top: 0,
          left: "-99999px",
          width: 1080,
          height: 1920,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <div
          ref={storyRef}
          style={{
            width: 1080,
            height: 1920,
            background: "var(--gai-white)",
            fontFamily: "'Playfair Display', serif",
            overflow: "hidden",
          }}
          className="notranslate"
        >
          {storyPoster}
        </div>
      </div>

      <div
        aria-hidden
        style={{
          position: "fixed",
          top: 0,
          left: "-99999px",
          width: 1200,
          height: 630,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <div
          ref={postRef}
          style={{
            width: 1200,
            height: 630,
            background: "var(--gai-white)",
            fontFamily: "'Playfair Display', serif",
            overflow: "hidden",
          }}
          className="notranslate"
        >
          {postPoster}
        </div>
      </div>
    </>
  );
}
